import { API_BASE } from '../config';
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './pages.css';
import './UserManager.css';
import UserExcelImport from './UserExcelImport';
import AssignmentExcelImport from './AssignmentExcelImport';
import { AssignedSitesDetail, AssignedSitesSummary } from './AssignedSites';
import { GradeFilter, GradeSelect, HiddenGradeNotices, gradeLabel, matchesGrade, useEvaluatorGrades } from './EvaluatorGrade';

const VULNERABILITY_LABELS = { low_vision: '저시력', blind: '전맹', unspecified: '유형미지정' };
const VULNERABILITY_FILTER_LABELS = { '0': '일반', '1': '취약 전체', ...VULNERABILITY_LABELS };
const vulnerabilityLabel = user => Number(user.is_vulnerable) === 1
  ? VULNERABILITY_LABELS[user.vulnerability_type] || VULNERABILITY_LABELS.unspecified
  : '일반';
const matchesVulnerability = (user, filter) => {
  if (!filter) return true;
  const vulnerable = Number(user.is_vulnerable) === 1;
  if (filter === '0') return !vulnerable;
  if (filter === '1') return vulnerable;
  if (filter === 'unspecified') return vulnerable && !user.vulnerability_type;
  return vulnerable && user.vulnerability_type === filter;
};

const UserManager = () => {
  const [users, setUsers] = useState([]);
  const grades = useEvaluatorGrades((id, grade) => setUsers(previous => previous.map(user => String(user.user_id) === String(id) ? { ...user, ...grade } : user)));
  const [stats, setStats] = useState({});
  const [name, setName] = useState('');
  const [sex, setSex] = useState('');
  const [birth, setBirth] = useState('');
  const [isVulnerable, setIsVulnerable] = useState(false);
  const [vulnerabilityType, setVulnerabilityType] = useState('');
  const [phone, setPhone] = useState('');
  const navigate = useNavigate();

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/');
  };

  const [editingUserIds, setEditingUserIds] = useState([]);
  const [editForms, setEditForms] = useState({});
  const [expandedUserIds, setExpandedUserIds] = useState([]); // 담당 사이트 전체 목록을 펼친 사용자

  // 사용자 목록 정렬 상태
  const [sortConfig, setSortConfig] = useState({
    key: '',
    direction: 'asc'
  });

  // 사용자 이름 검색
  const [nameSearch, setNameSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [activePanel, setActivePanel] = useState('');
  const [listExpanded, setListExpanded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState('');
  const [actionError, setActionError] = useState('');
  const [actionBusy, setActionBusy] = useState(false);
  const requestId = useRef(0);
  const listScroll = useRef(null);
  const workspace = useRef(null);
  useEffect(() => { if (activePanel && workspace.current) workspace.current.scrollTop = 0; }, [activePanel]);

  const toggleExpandedUser = (userId) => {
    setExpandedUserIds(prev => prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]);
  };
  const removeAssignments = async (userId, assignmentIds) => {
    const res = await fetch(`${API_BASE}/api/admin/assignments`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
      body: JSON.stringify({ assignment_ids: assignmentIds }),
    });
    const result = await res.json().catch(() => ({}));
    if (!res.ok || !result.success) throw new Error(result.error || '배정 해제에 실패했습니다. 다시 시도해 주세요.');
    setUsers(prev => prev.map(user => user.user_id === userId
      ? { ...user, sites: user.sites.filter(site => !assignmentIds.includes(site.assignment_id)) }
      : user));
    await fetchUsers(true);
  };

  // 필터
  const [filterProgramId, setFilterProgramId] = useState(''); // ''=전체
  const [filterSiteId, setFilterSiteId]       = useState(''); // ''=전체
  const [filterSex, setFilterSex] = useState('');
  const [filterAgeGroup, setFilterAgeGroup] = useState('');
  const [filterVulnerable, setFilterVulnerable] = useState('');
  const [filterGrade, setFilterGrade] = useState('');
  // 배정
  const [assignProgramId, setAssignProgramId] = useState('');
  const [assignSiteId, setAssignSiteId]       = useState('');

  // 목록
  const [programs, setPrograms] = useState([]);
  const [sitesForFilter, setSitesForFilter]   = useState([]);
  const [sitesForAssign, setSitesForAssign]   = useState([]);

  // 한국 시간 기준 연도를 사용해 서버와 연말·연초 분류를 맞춘다.
  const CURRENT_YEAR = useMemo(() => Number(new Intl.DateTimeFormat('en-US', { timeZone: 'Asia/Seoul', year: 'numeric' }).format(new Date())), []);

  // ✅ 프런트에서 고정으로 쓸 연령대 라벨
  const AGE_GROUPS = ['20대', '30대', '40대', '50대', '60대', '70대 이상'];

  // 태어난 해를 1세로 계산하는 한국식 나이
  const ageFromBirthYear = useCallback((birthYear) => {
    if (birthYear === null || birthYear === undefined) return null;
    const by = Number(birthYear);
    if (!Number.isInteger(by) || by < 1900 || by > CURRENT_YEAR) return null;
    const age = CURRENT_YEAR - by + 1;
    return (age >= 1 && age <= 120) ? age : null;
  }, [CURRENT_YEAR]);

  // ✅ 나이를 연령대로 매핑(20~29만 20대)
  const ageToGroup = useCallback((age) => {
    if (age === null) return null;
    if (age >= 20 && age <= 29) return '20대';
    if (age >= 30 && age <= 39) return '30대';
    if (age >= 40 && age <= 49) return '40대';
    if (age >= 50 && age <= 59) return '50대';
    if (age >= 60 && age <= 69) return '60대';
    if (age >= 70) return '70대 이상';  // 80세 이상도 여기에 포함
    return null; // 0~19세, 음수 등은 그룹 제외
  }, []);

  // 사용자 목록 정렬
  const handleSort = (key) => {
    setPage(1);
    setSortConfig(prev => ({
      key,
      direction:
        prev.key === key && prev.direction === 'asc'
          ? 'desc'
          : 'asc'
    }));
  };

  const filteredUsers = useMemo(() => {
    const keyword = nameSearch.trim().toLowerCase();
    return users.filter(user =>
      matchesGrade(user, filterGrade) &&
      (!keyword || String(user.name || '').toLowerCase().includes(keyword)) &&
      (!filterSex || user.sex === filterSex) &&
      (!filterAgeGroup || ageToGroup(ageFromBirthYear(user.birth)) === filterAgeGroup) &&
      matchesVulnerability(user, filterVulnerable)
    );
  }, [users, nameSearch, filterSex, filterAgeGroup, filterVulnerable, filterGrade, ageFromBirthYear, ageToGroup]);

  const hasListFilters = Boolean(nameSearch || filterProgramId || filterSiteId || filterSex || filterAgeGroup || filterVulnerable !== '' || filterGrade);
  const resetListFilters = () => {
    setNameSearch('');
    setFilterProgramId('');
    setFilterSiteId('');
    setFilterSex('');
    setFilterAgeGroup('');
    setFilterVulnerable('');
    setFilterGrade('');
  };

  const sortedUsers = useMemo(() => {
    const list = [...filteredUsers];

    if (!sortConfig.key) return list;

    return list.sort((a, b) => {
      let aValue;
      let bValue;

      switch (sortConfig.key) {
        case 'name':
          aValue = a.name || '';
          bValue = b.name || '';
          break;

        case 'sex':
          aValue = a.sex || '';
          bValue = b.sex || '';
          break;

        case 'age':
          aValue = ageFromBirthYear(a.birth) ?? -1;
          bValue = ageFromBirthYear(b.birth) ?? -1;
          break;

        case 'created_at':
          aValue = a.created_at || '';
          bValue = b.created_at || '';
          break;

        case 'response_count':
          aValue = Number(a.response_count || 0);
          bValue = Number(b.response_count || 0);
          break;

        case 'login_visited':
          aValue = a.login_visited || '';
          bValue = b.login_visited || '';
          break;

        case 'response_visited':
          aValue = a.response_visited || '';
          bValue = b.response_visited || '';
          break;

        default:
          return 0;
      }

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.direction === 'asc'
          ? aValue - bValue
          : bValue - aValue;
      }

      return sortConfig.direction === 'asc'
        ? String(aValue).localeCompare(String(bValue), 'ko')
        : String(bValue).localeCompare(String(aValue), 'ko');
    });
  }, [filteredUsers, sortConfig, ageFromBirthYear]);

  // ✅ 사용자 목록에서 통계를 다시 계산(성별 구분 포함)
  const buildAgeStatsBySex = (userList) => {
    const base = { 취약계층: { low_vision: 0, blind: 0, unspecified: 0, 합계: 0 }, 기타: { 합계: 0 } };
    AGE_GROUPS.forEach(g => { base[g] = { 남: 0, 여: 0, 합계: 0 }; });

    for (const u of userList) {
      if (Number(u.is_vulnerable) === 1) {
        base.취약계층.합계 += 1;
        const type = u.vulnerability_type === 'low_vision' || u.vulnerability_type === 'blind' ? u.vulnerability_type : 'unspecified';
        base.취약계층[type] += 1;
        continue;
      }
      const age = ageFromBirthYear(u.birth);
      const group = ageToGroup(age);
      const sexKey = (u.sex === '남' || u.sex === '여') ? u.sex : null;
      if (!group || !sexKey) { base.기타.합계 += 1; continue; }
      base[group][sexKey] += 1;
      base[group].합계 += 1;
    }
    return base;
  };

  const pageCount = Math.max(1, Math.ceil(sortedUsers.length / pageSize));
  const currentPage = Math.min(page, pageCount);
  const pageStart = (currentPage - 1) * pageSize;
  const pageUsers = sortedUsers.slice(pageStart, pageStart + pageSize);
  const selectedIds = new Set(editingUserIds);
  const allPageSelected = pageUsers.length > 0 && pageUsers.every(user => selectedIds.has(user.user_id));
  const somePageSelected = pageUsers.some(user => selectedIds.has(user.user_id));
  const hiddenSelectionCount = editingUserIds.filter(id => !pageUsers.some(user => user.user_id === id)).length;
  const allResultsSelected = sortedUsers.length > 0 && sortedUsers.every(user => selectedIds.has(user.user_id));
  const firstPageNumber = Math.max(1, Math.min(currentPage - 2, pageCount - 4));
  const pageNumbers = Array.from({ length: Math.min(5, pageCount) }, (_, index) => firstPageNumber + index);

  useEffect(() => { setPage(1); }, [nameSearch, filterProgramId, filterSiteId, filterSex, filterAgeGroup, filterVulnerable, filterGrade, pageSize]);
  useEffect(() => { setPage(previous => Math.min(previous, pageCount)); }, [pageCount]);
  useEffect(() => { if (listScroll.current) listScroll.current.scrollTop = 0; }, [currentPage, pageSize, nameSearch, filterSex, filterAgeGroup, filterVulnerable, filterGrade, sortConfig]);

  const clearSelection = () => {
    setEditingUserIds([]);
    setEditForms({});
    setActionError('');
  };

  const selectUsers = (list, checked) => {
    const ids = new Set(list.map(user => user.user_id));
    setEditingUserIds(previous => checked
      ? [...new Set([...previous, ...ids])]
      : previous.filter(id => !ids.has(id)));
    setEditForms(previous => {
      const next = { ...previous };
      list.forEach(user => {
        if (!checked) delete next[user.user_id];
        else if (!next[user.user_id]) next[user.user_id] = {
          name: user.name, sex: user.sex, birth: user.birth, phone: user.phone,
          is_vulnerable: Boolean(Number(user.is_vulnerable)),
          vulnerability_type: Number(user.is_vulnerable) === 1 ? user.vulnerability_type || null : null,
        };
      });
      return next;
    });
  };

  const handleCheckboxChange = (userId, checked) => selectUsers(users.filter(user => user.user_id === userId), checked);

  // ✅ 사용자 목록을 불러온 후, 통계를 프런트에서 재계산
  const fetchUsers = async (preserveSelection = false) => {
    const currentRequest = ++requestId.current;
    setLoading(true);
    setListError('');
    try {
      const token = localStorage.getItem('token');
      const qs = new URLSearchParams();
      if (filterProgramId) qs.set('program_id', filterProgramId);
      if (filterSiteId)    qs.set('site_id',     filterSiteId);
      const url = `${API_BASE}/api/admin/users${qs.toString() ? `?${qs}` : ''}`;

      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || '사용자 목록을 불러오지 못했습니다.');
      if (currentRequest !== requestId.current) return;
      const nextUsers = grades.mergeGrades(data.users || []);
      setUsers(nextUsers);
      if (preserveSelection) {
        const remaining = new Set(nextUsers.map(user => user.user_id));
        setEditingUserIds(previous => previous.filter(id => remaining.has(id)));
        setEditForms(previous => Object.fromEntries(Object.entries(previous).filter(([id]) => remaining.has(Number(id)))));
      } else clearSelection();

      // ⚠️ 서버에서 주는 stats 대신, 프런트에서 정확 기준으로 재계산
      setStats(buildAgeStatsBySex(nextUsers));
    } catch (err) {
      if (currentRequest === requestId.current) setListError(err.message);
    } finally {
      if (currentRequest === requestId.current) setLoading(false);
    }
  };

  useEffect(() => {
    clearSelection();
    setUsers([]);
    setStats({});
    fetchUsers();
    return () => { requestId.current += 1; };
  }, [filterProgramId, filterSiteId]);

  const updateEditForm = (userId, field, value) => {
    setEditForms(prev => ({
      ...prev,
      [userId]: {
        ...prev[userId],
        [field]: value,
        ...(field === 'is_vulnerable' || field === 'vulnerability_type' ? { vulnerabilityEdited: true } : {}),
        ...(field === 'is_vulnerable' && !value ? { vulnerability_type: null } : {})
      }
    }));
  };

  const handleRegister = async () => {
    if (!name || !sex || !birth || !phone) {
      alert('모든 항목을 입력해주세요');
      return;
    }
    if (isVulnerable && !vulnerabilityType) {
      alert('취약계층 유형을 선택해주세요.');
      return;
    }

    const body = { name, sex, birth: parseInt(birth), phone, role: 'user', is_vulnerable: isVulnerable, vulnerability_type: isVulnerable ? vulnerabilityType : null };
    try {
      const res = await fetch(`${API_BASE}/api/admin/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        alert('사용자 등록 성공!');
        setName('');
        setSex('');
        setBirth('');
        setPhone('');
        setIsVulnerable(false);
        setVulnerabilityType('');
        await fetchUsers(true); // 기존 사용자의 선택·수정 내용은 유지
      } else {
        const result = await res.json();
        alert('등록 실패: ' + (result.error || '알 수 없는 오류'));
      }
    } catch (err) {
      console.error('등록 중 오류:', err);
      alert('네트워크 오류 발생: ' + err.message);
    }
  };

  const handleBulkSave = async () => {
    if (actionBusy || !editingUserIds.length) return;
    const token = localStorage.getItem('token');
    setActionBusy(true);
    setActionError('');
    try {
      for (const userId of editingUserIds) {
        const { is_vulnerable, vulnerability_type, vulnerabilityEdited, ...profile } = editForms[userId];
        const form = vulnerabilityEdited
          ? { ...profile, is_vulnerable: Boolean(is_vulnerable), vulnerability_type: is_vulnerable ? vulnerability_type || null : null }
          : profile;
        const res = await fetch(`${API_BASE}/api/admin/users/${userId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(form)
        });
        if (!res.ok) {
          const result = await res.json().catch(() => ({}));
          throw new Error(result.error || '사용자 수정에 실패했습니다.');
        }
      }

      alert('모든 사용자 수정 완료!');
      clearSelection();
      await fetchUsers();
    } catch (err) {
      setActionError(err.message);
    } finally { setActionBusy(false); }
  };

  const handleBulkDeleteUsers = async () => {
    if (actionBusy || !editingUserIds.length) return;
    const confirmed = window.confirm(`선택한 사용자 ${editingUserIds.length}명을 삭제하시겠습니까? 다른 페이지에서 선택한 사용자도 포함됩니다. 이 작업은 되돌릴 수 없습니다.`);
    if (!confirmed) return;

    const token = localStorage.getItem('token');
    setActionBusy(true);
    setActionError('');
    try {
      for (const userId of editingUserIds) {
        const res = await fetch(`${API_BASE}/api/admin/users/${userId}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok) {
          const result = await res.json().catch(() => ({}));
          throw new Error(result.error || '사용자 삭제에 실패했습니다.');
        }
        setEditingUserIds(previous => previous.filter(id => id !== userId));
      }

      alert('삭제 완료!');
      clearSelection();
      await fetchUsers();
    } catch (err) {
      setActionError(err.message);
      await fetchUsers(true);
    } finally { setActionBusy(false); }
  };

  const handleAssign = async () => {
    if (actionBusy || !editingUserIds.length || !assignProgramId) return;
    setActionBusy(true);
    setActionError('');
    try {
      const res = await fetch(`${API_BASE}/api/admin/assignments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ user_ids: editingUserIds, program_id: Number(assignProgramId), site_id: assignSiteId ? Number(assignSiteId) : '' }),
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.error || '배정에 실패했습니다.');
      alert(`배정 완료! (신규 ${result.inserted}건)`);
      await fetchUsers(true);
    } catch (err) { setActionError(err.message); }
    finally { setActionBusy(false); }
  };

  useEffect(() => {
    document.body.classList.add('result-page-bg');
    return () => document.body.classList.remove('result-page-bg');
  }, []);

  useEffect(() => {
    (async () => {
      const token = localStorage.getItem('token');
      const p = await fetch(`${API_BASE}/api/programs`, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(r => r.json());
      setPrograms(p);
    })();
  }, []);

  // 필터용 사이트 (사업 선택 시)
  useEffect(() => {
    (async () => {
      const token = localStorage.getItem('token');
      setFilterSiteId('');
      if (!filterProgramId) { setSitesForFilter([]); return; }
      const s = await fetch(`${API_BASE}/api/sites?program_id=${filterProgramId}`, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(r => r.json());
      setSitesForFilter(s);
    })();
  }, [filterProgramId]);

  // 배정용 사이트 (배정 사업 선택 시)
  useEffect(() => {
    (async () => {
      const token = localStorage.getItem('token');
      setAssignSiteId('');
      if (!assignProgramId) { setSitesForAssign([]); return; }
      const s = await fetch(`${API_BASE}/api/sites?program_id=${assignProgramId}`, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(r => r.json());
      setSitesForAssign(s);
    })();
  }, [assignProgramId]);

  return (
    <div className="user-management-page">
        <div className="top-right-button-group">
            <button onClick={() => navigate('/dashboard')} className="navigate-button">대시보드</button>
            <button onClick={() => navigate('/user')} className="navigate-button primary">
            평가단 관리
            </button>
            <button onClick={() => navigate('/site')} className="navigate-button">
            사이트 관리
            </button>
            <button onClick={() => navigate('/admin')} className="navigate-button">
            설문 관리
            </button>
            <button onClick={() => navigate('/responses')} className="navigate-button">
            응답 관리
            </button>
            <button onClick={() => navigate('/results')} className="navigate-button">
            통계 보기
            </button>
            <button onClick={() => navigate('/admin/login-notice')} className="navigate-button">로그인 공지 관리</button>
            <button onClick={handleLogout} className="navigate-button logout-button">
            로그아웃
            </button>
      </div>

      <div className={`survey-result-container user-manager${listExpanded ? ' is-list-expanded' : ''}`}>
        <header className="user-manager-header">
          <div><h2>사용자 관리</h2><p>평가단을 찾고, 정보를 수정하거나 사이트를 배정하세요.</p></div>
          <div className="user-manager-actions">
            {[['register', '사용자 추가'], ['excel', '엑셀 등록'], ['assign', '엑셀 배정']].map(([panel, label]) => (
              <button key={panel} type="button" className={panel === 'register' ? 'user-manager-primary' : ''}
                aria-expanded={activePanel === panel} aria-controls={`user-panel-${panel}`} disabled={actionBusy}
                onClick={() => setActivePanel(previous => previous === panel ? '' : panel)}>{label}</button>
            ))}
          </div>
        </header>
        <div className="user-manager-workspace" ref={workspace}>

        <section id="user-panel-register" className="user-manager-panel" hidden={activePanel !== 'register'} aria-label="사용자 추가">
        {/* 사용자 추가 */}
        <h3>사용자 추가</h3>
        <div className="user-input-row">
          <input className="user-input" placeholder="이름" value={name} onChange={e => setName(e.target.value)} style={{ width: "150px" }}/>
          <select className="user-input" value={sex} onChange={e => setSex(e.target.value)}>
            <option value="">성별</option>
            <option value="남">남</option>
            <option value="여">여</option>
          </select>
          <input
            className="user-input"
            type="number"
            placeholder="출생년도(예: 1992)"
            value={birth}
            onChange={e => setBirth(e.target.value)}
            min="1900"
            max={CURRENT_YEAR}
            style={{ width: "150px" }}
          />
          <input className="user-input" placeholder="비밀번호" value={phone} onChange={e => setPhone(e.target.value)} />
          <label style={{ display: "inline-flex", alignItems: "center", gap: "6px", whiteSpace: "nowrap" }}><input type="checkbox" checked={isVulnerable} onChange={e => { setIsVulnerable(e.target.checked); if (!e.target.checked) setVulnerabilityType(''); }} /> 취약계층</label>
          {isVulnerable && <select className="user-input user-vulnerability-select" aria-label="등록 취약계층 유형" value={vulnerabilityType} onChange={e => setVulnerabilityType(e.target.value)}>
            <option value="">유형 선택 (필수)</option><option value="low_vision">저시력</option><option value="blind">전맹</option>
          </select>}
          <button className="user-input-button" onClick={handleRegister}>등록하기</button>
        </div>
        </section>
        <section id="user-panel-excel" className="user-manager-panel" hidden={activePanel !== 'excel'} aria-label="엑셀 등록">
          <UserExcelImport onRegistered={() => fetchUsers(true)} />
        </section>
        <section id="user-panel-assign" className="user-manager-panel" hidden={activePanel !== 'assign'} aria-label="엑셀 배정">
          <AssignmentExcelImport onAssigned={() => fetchUsers(true)} />
        </section>
        <details className="user-registration-stats">
          <summary><strong>사용자 등록 현황</strong><span>{filterProgramId || filterSiteId ? '사업·사이트 필터 기준' : '전체'} {users.length}명</span><span className="user-stats-hint">연령·성별 상세 보기</span></summary>
          <table className="user-styled-table">
            <thead>
              <tr><th></th><th>남</th><th>여</th><th>합계</th></tr>
            </thead>
            <tbody>
              {/* ✅ 프런트에서 재계산한 통계로 렌더 */}
              {AGE_GROUPS.map((age) => (
                <tr key={age}>
                  <td>{age}</td>
                  <td>{stats?.[age]?.남 ?? 0}</td>
                  <td>{stats?.[age]?.여 ?? 0}</td>
                  <td>{stats?.[age]?.합계 ?? 0}</td>
                </tr>
              ))}
              <tr className="vulnerability-row"><td>취약계층</td><td colSpan="2">—</td><td>{stats?.취약계층?.합계 ?? 0}</td></tr>
              {Object.entries(VULNERABILITY_LABELS).map(([type, label]) => <tr key={type} data-vulnerability={type} className="user-vulnerability-subrow"><td>{label}</td><td colSpan="2">—</td><td>{stats?.취약계층?.[type] ?? 0}</td></tr>)}
              <tr><td>기타·미분류</td><td colSpan="2">—</td><td>{stats?.기타?.합계 ?? 0}</td></tr>
            </tbody>
          </table>
          <p className="demographic-note">취약계층은 유형별로만 집계하며 연령·성별 인원에서 제외합니다. 기타·미분류는 일반 평가자 중 20세 미만 또는 연령·성별 정보가 없는 인원입니다.</p>
        </details>

        {/* 사용자 목록 */}
        <section className="user-list-section" aria-label="사용자 목록" aria-busy={loading}>
          <div className="user-list-toolbar">
            <label htmlFor="filter-program">사업
              <select id="filter-program" value={filterProgramId} disabled={actionBusy}
                onChange={e => { setFilterSiteId(''); setFilterProgramId(e.target.value); }}>
                <option value="">전체 사업</option>
                {programs.map(p => <option key={p.program_id} value={p.program_id}>{p.name}</option>)}
              </select>
            </label>
            <label htmlFor="filter-site">사이트
              <select id="filter-site" value={filterSiteId} disabled={!filterProgramId || actionBusy}
                onChange={e => setFilterSiteId(e.target.value)}>
                <option value="">전체 사이트</option>
                {sitesForFilter.map(s => <option key={s.site_id} value={s.site_id}>{s.site_name}</option>)}
              </select>
            </label>
            <label htmlFor="user-name-search" className="user-search-label">이름 검색
            <input
              id="user-name-search"
              className="user-input"
              type="text"
              placeholder="이름 검색"
              value={nameSearch}
              onChange={(e) => setNameSearch(e.target.value)}
            />
            </label>
            {nameSearch && (
              <button
                type="button"
                className="user-input-button"
                onClick={() => setNameSearch('')}
              >
                검색 초기화
              </button>
            )}
            <label htmlFor="user-page-size" className="user-page-size-label">표시 인원
              <select id="user-page-size" value={pageSize} onChange={e => setPageSize(Number(e.target.value))}>
                <option value={20}>20명씩</option><option value={50}>50명씩</option><option value={100}>100명씩</option>
              </select>
            </label>
            <div className="user-demographic-filters">
              <label htmlFor="filter-sex">성별
                <select id="filter-sex" value={filterSex} disabled={actionBusy} onChange={e => setFilterSex(e.target.value)}>
                  <option value="">전체 성별</option><option value="남">남</option><option value="여">여</option>
                </select>
              </label>
              <label htmlFor="filter-age-group">연령대
                <select id="filter-age-group" value={filterAgeGroup} disabled={actionBusy} onChange={e => setFilterAgeGroup(e.target.value)}>
                  <option value="">전체 연령대</option>
                  {AGE_GROUPS.map(group => <option key={group} value={group}>{group}</option>)}
                </select>
              </label>
              <label htmlFor="filter-vulnerable">취약계층
                <select id="filter-vulnerable" value={filterVulnerable} disabled={actionBusy} onChange={e => setFilterVulnerable(e.target.value)}>
                  <option value="">전체</option><option value="0">일반</option><option value="1">취약 전체</option>
                  {Object.entries(VULNERABILITY_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </select>
              </label>
              {hasListFilters && <button type="button" disabled={actionBusy} onClick={resetListFilters}>필터 초기화</button>}
              <GradeFilter id="filter-grade" value={filterGrade} disabled={actionBusy} onChange={setFilterGrade} />
            </div>
          </div>
          <div className="user-list-heading">
            <h3>사용자 목록 <span>{filteredUsers.length}명</span></h3>
            {listExpanded ? <span className="user-list-context">
              {[programs.find(p => String(p.program_id) === filterProgramId)?.name, sitesForFilter.find(s => String(s.site_id) === filterSiteId)?.site_name, nameSearch.trim() ? `이름: ${nameSearch.trim()}` : '', filterSex ? `성별: ${filterSex}` : '', filterAgeGroup, filterVulnerable !== '' ? `취약계층: ${VULNERABILITY_FILTER_LABELS[filterVulnerable]}` : '', filterGrade ? `등급: ${gradeLabel(filterGrade)}` : ''].filter(Boolean).join(' · ') || '전체 사용자'}
            </span> : editingUserIds.length > 0 && <span className="user-list-context">사업·사이트 변경 시 선택·수정 내용이 초기화됩니다.</span>}
            <div className="user-list-tools">
              {editingUserIds.length > 0 && !allResultsSelected && sortedUsers.length > 0 && <button type="button" disabled={actionBusy || loading} onClick={() => selectUsers(sortedUsers, true)}>검색 결과 {sortedUsers.length}명 전체 선택</button>}
              <button type="button" className="user-list-expand" aria-pressed={listExpanded} onClick={() => setListExpanded(previous => !previous)}>{listExpanded ? '기본 화면으로' : '목록 크게 보기'}</button>
            </div>
          </div>
          {listError && <p className="user-manager-error" role="alert">{listError} <button onClick={() => fetchUsers(true)}>다시 불러오기</button></p>}
          <HiddenGradeNotices controller={grades} visibleUsers={pageUsers} />
          <div className="user-list-scroll" ref={listScroll}>
          <table className="user-styled-table fixed-table user-list-table">
            <colgroup>
              {[3, 8, 4, 5, 9, 9, 8, 8, 15, 6, 12, 13].map((width, index) => <col key={index} style={{ width: index === 6 ? '132px' : `${width}%` }} />)}
            </colgroup>
            <thead>
              <tr>
                <th scope="col"><input type="checkbox" aria-label="현재 페이지 전체 선택" checked={allPageSelected}
                  ref={element => { if (element) element.indeterminate = somePageSelected && !allPageSelected; }}
                  disabled={loading || actionBusy || !pageUsers.length} onChange={e => selectUsers(pageUsers, e.target.checked)} /></th>
                {[['name', '이름'], ['sex', '성별'], ['age', '나이'], ['', '비밀번호'], ['', '취약계층'], ['', '평가자 등급'], ['created_at', '등록일'], ['', '담당 웹사이트/앱'], ['response_count', '완료 수'], ['login_visited', '최근 접속일'], ['response_visited', '최근 응답일']].map(([key, label]) => (
                  <th key={label} scope="col" aria-sort={key ? (sortConfig.key === key ? (sortConfig.direction === 'asc' ? 'ascending' : 'descending') : 'none') : undefined}>
                    {key ? <button type="button" onClick={() => handleSort(key)}>{label} <span aria-hidden="true">{sortConfig.key === key ? (sortConfig.direction === 'asc' ? '▲' : '▼') : '↕'}</span></button> : label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading || sortedUsers.length === 0 ? (
                <tr>
                  <td colSpan="12">
                    {loading ? '사용자 목록을 불러오는 중…' : listError ? '목록을 불러오지 못했습니다. 다시 불러오기를 눌러주세요.' : hasListFilters ? '검색 결과가 없습니다' : '등록된 사용자가 없습니다'}
                  </td>
                </tr>
              ) : (
                pageUsers.map(user => (
                  <Fragment key={user.user_id}>
                    <tr className={`${expandedUserIds.includes(user.user_id) ? 'is-expanded' : ''} ${selectedIds.has(user.user_id) ? 'is-selected' : ''}`}>
                      <td>
                        <input
                          className="user-input"
                          type="checkbox"
                          aria-label={`${user.name} 선택`}
                          disabled={actionBusy}
                          checked={selectedIds.has(user.user_id)}
                          onChange={(e) => handleCheckboxChange(user.user_id, e.target.checked)}
                        />
                      </td>

                      {editingUserIds.includes(user.user_id) ? (
                        <>
                          <td>
                            <input
                              aria-label={`${user.name} 이름`}
                              disabled={actionBusy}
                              className="user-input"
                              value={editForms[user.user_id]?.name || ''}
                              onChange={(e) => updateEditForm(user.user_id, 'name', e.target.value)}
                            />
                          </td>
                          <td>
                            <select
                              aria-label={`${user.name} 성별`}
                              disabled={actionBusy}
                              className="user-input"
                              value={editForms[user.user_id]?.sex || ''}
                              onChange={(e) => updateEditForm(user.user_id, 'sex', e.target.value)}
                            >
                              <option value="남">남</option>
                              <option value="여">여</option>
                            </select>
                          </td>
                          <td>
                            <input
                              aria-label={`${user.name} 출생년도`}
                              disabled={actionBusy}
                              className="user-input"
                              type="number"
                              value={editForms[user.user_id]?.birth || ''}
                              onChange={(e) => updateEditForm(user.user_id, 'birth', e.target.value)}
                            />
                          </td>
                          <td>
                            <input
                              aria-label={`${user.name} 비밀번호`}
                              disabled={actionBusy}
                              className="user-input"
                              value={editForms[user.user_id]?.phone || ''}
                              onChange={(e) => updateEditForm(user.user_id, 'phone', e.target.value)}
                            />
                          </td>
                        </>
                      ) : (
                        <>
                          <td>{user.name}</td>
                          <td>{user.sex}</td>
                          <td>{ageFromBirthYear(user.birth) ?? '-'}</td>
                          <td>{user.phone}</td>
                        </>
                      )}

                      <td>{editingUserIds.includes(user.user_id)
                        ? <div className="user-vulnerability-edit">
                          <label><input type="checkbox" disabled={actionBusy} aria-label={`${user.name} 취약계층`} checked={editForms[user.user_id]?.is_vulnerable || false} onChange={e => updateEditForm(user.user_id, 'is_vulnerable', e.target.checked)} /> 취약</label>
                          {editForms[user.user_id]?.is_vulnerable && <select className="user-input user-vulnerability-select" disabled={actionBusy} aria-label={`${user.name} 취약계층 유형`} value={editForms[user.user_id]?.vulnerability_type || ''} onChange={e => updateEditForm(user.user_id, 'vulnerability_type', e.target.value || null)}>
                            <option value="">유형미지정</option><option value="low_vision">저시력</option><option value="blind">전맹</option>
                          </select>}
                        </div>
                        : vulnerabilityLabel(user)}</td>
                      <td><GradeSelect user={user} controller={grades} /></td>
                      <td>{user.created_at?.slice(0, 10)}</td>
                      <td>
                        <AssignedSitesSummary
                          sites={user.sites}
                          expanded={expandedUserIds.includes(user.user_id)}
                          onToggle={() => toggleExpandedUser(user.user_id)}
                        />
                      </td>
                      <td>{user.response_count}개</td>
                      <td>
                        {user.login_visited
                          ? user.login_visited.slice(0, 19).replace('T', ' ')
                          : '-'}
                      </td>
                      <td>
                        {user.response_visited
                          ? user.response_visited.slice(0, 19).replace('T', ' ')
                          : '-'}
                      </td>
                    </tr>
                    {expandedUserIds.includes(user.user_id) && user.sites?.length > 0 && (
                      <tr className="assigned-sites-detail-row">
                        <td colSpan="12">
                          <AssignedSitesDetail sites={user.sites} userName={user.name} onRemove={ids => removeAssignments(user.user_id, ids)} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))
              )}
            </tbody>
          </table>

          </div>
          <div className="user-list-pagination">
            <span className="user-list-range" role="status">{sortedUsers.length ? pageStart + 1 : 0}–{Math.min(pageStart + pageSize, sortedUsers.length)} / {sortedUsers.length}명</span>
            <nav aria-label="사용자 목록 페이지">
              <button type="button" disabled={currentPage === 1 || loading} onClick={() => setPage(1)}>처음</button>
              <button type="button" disabled={currentPage === 1 || loading} onClick={() => setPage(currentPage - 1)}>이전</button>
              {pageNumbers.map(number => <button type="button" key={number} aria-label={number + '페이지'} aria-current={currentPage === number ? 'page' : undefined} disabled={loading} onClick={() => setPage(number)}>{number}</button>)}
              <button type="button" disabled={currentPage === pageCount || loading} onClick={() => setPage(currentPage + 1)}>다음</button>
              <button type="button" disabled={currentPage === pageCount || loading} onClick={() => setPage(pageCount)}>마지막</button>
            </nav>
            <span className="user-page-count">{currentPage} / {pageCount} 페이지</span>
          </div>
        </section>
        </div>
        {editingUserIds.length > 0 && <section className="user-batch-bar" aria-label="선택한 사용자 작업" aria-busy={actionBusy}>
          <div className="user-batch-summary" title="페이지를 이동해도 선택과 수정 내용이 유지됩니다. 사업·사이트 변경 시 선택이 해제됩니다.">
            <strong role="status">{editingUserIds.length}명 선택</strong>
            {hiddenSelectionCount > 0 && <span>현재 페이지 밖 {hiddenSelectionCount}명 포함</span>}
            <button type="button" onClick={clearSelection} disabled={actionBusy}>선택 해제</button>
          </div>
          <div className="user-batch-actions">
            <label htmlFor="assign-program">사이트 배정
              <select id="assign-program" value={assignProgramId} disabled={actionBusy || loading} onChange={e => { setAssignSiteId(''); setAssignProgramId(e.target.value); }}>
                <option value="">사업 선택</option>
                {programs.map(p => <option key={p.program_id} value={p.program_id}>{p.name}</option>)}
              </select>
            </label>
            <select id="assign-site" aria-label="배정할 사이트" value={assignSiteId} disabled={!assignProgramId || actionBusy || loading} onChange={e => setAssignSiteId(e.target.value)}>
              <option value="">사업 내 전체 사이트</option>
              {sitesForAssign.map(s => <option key={s.site_id} value={s.site_id}>{s.site_name}</option>)}
            </select>
            <button type="button" className="user-manager-primary" disabled={!assignProgramId || actionBusy || loading} onClick={handleAssign}>배정하기</button>
            <div className="user-batch-edit">
              <button type="button" onClick={handleBulkSave} disabled={actionBusy || loading}>일괄 저장</button>
              <button type="button" className="user-manager-danger" onClick={handleBulkDeleteUsers} disabled={actionBusy || loading}>삭제</button>
            </div>
          </div>
          {actionBusy && <p role="status">선택한 사용자에게 적용 중입니다…</p>}
          {actionError && <p className="user-manager-error" role="alert">{actionError}</p>}
        </section>}
      </div>
    </div>
  );
};

export default UserManager;
